import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserCohort } from "@/hooks/cohort/useUserCohort";
import { logger } from "@/services/logger";
import { Account as AccountType } from "@/types/userProgress";
import {
  UserProgressService,
  UserProgressState,
  TransactionViewMode,
} from "@/services/userProgressService";
import { CohortService } from "@/services/cohortService";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import { useTransactionContext } from "@/contexts/TransactionContext";
import {
  useTransactionViewStore,
  viewScopeKey,
} from "@/store/transactionViewStore";

// Incrementing counter used to give each hook instance a short, readable ID
// for debug logging (e.g. "hook-1", "hook-2").
let hookInstanceCounter = 0;

// Per-instance write cooldown: each hook instance tracks when it last wrote
// to Firestore.  Its own onSnapshot callback skips updates during the cooldown
// to avoid overwriting optimistic in-memory state.  Instances that haven't
// written (e.g. Financial Statements) always accept updates immediately.
const LOCAL_WRITE_COOLDOWN_MS = 2000;

// The set participants actually work in. "custom" only counts while the cohort
// has custom transactions: getUserTransactions falls back to the primary list
// when the custom one is empty, and pairing that list with the custom progress
// fields mismatches every placed amount (already-placed amounts reappear in
// the sidebar as if they were never dragged).
const participantViewMode = (
  mode: string | undefined,
  transactionConfig: { custom?: { transactions?: unknown[] } } | undefined,
): "default" | "custom" =>
  mode === "custom" && (transactionConfig?.custom?.transactions?.length ?? 0) > 0
    ? "custom"
    : "default";

type Account = {
  id: string;
  name: string;
  balance: number;
  comment?: string;
  label?: string;
};

type Amount = {
  id: string;
  amount: number;
  formattedAmount: string;
  transactionId: number;
  type: string;
  originalColor?: string;
  isPositive?: boolean;
  date?: string;
  details?: string;
};

type DroppedAmounts = Record<
  string,
  Array<{
    id: string;
    amount: number;
    formattedAmount: string;
    transactionId: number;
    type: string;
    originalColor?: string;
    isPositive?: boolean;
    date?: string;
    details?: string;
  }>
>;

export const useUserProgress = (overrideCohortId?: string) => {
  const { currentUser } = useAuth();
  const { cohort: lookupCohort } = useUserCohort(overrideCohortId);
  const cohort = lookupCohort;
  const { currentTransactionIndex, setCurrentTransactionIndex } =
    useTransactionContext();
  // The mode the trainer set for the whole cohort…
  const [cohortViewMode, setCohortViewMode] = useState<"default" | "custom">(
    "default"
  );
  // Ref mirror so the cohort snapshot callback can compare against the current
  // value without re-subscribing on every change.
  const cohortViewModeRef = useRef<"default" | "custom">("default");

  // …and this user's own choice from TransactionViewSwitch, which wins when
  // they've made one.
  const scopeKey = viewScopeKey(currentUser?.id, cohort?.id);
  const viewOverride = useTransactionViewStore((s) =>
    scopeKey ? s.overrides[scopeKey] ?? null : null
  );
  // Whether the cohort currently has custom transactions; an override can only
  // pick the custom set while it exists (see participantViewMode).
  const [hasCustomSet, setHasCustomSet] = useState(false);
  const viewMode: "default" | "custom" =
    viewOverride === "custom" && !hasCustomSet
      ? "default"
      : viewOverride ?? cohortViewMode;

  const [isLoaded, setIsLoaded] = useState(false);
  // Ref mirror of isLoaded — used by the onSnapshot callback so it doesn't
  // need to be in the effect's dependency array.
  const isLoadedRef = useRef(false);
  // Tracks when this specific hook instance last wrote to Firestore.
  const lastWriteRef = useRef(0);
  const stampLocalWrite = useCallback(() => {
    lastWriteRef.current = Date.now();
  }, []);
  const [customTransactionIndex, setCustomTransactionIndex] = useState(0);
  const [highestTransactionIndex, setHighestTransactionIndex] = useState(0);
  const [customHighestTransactionIndex, setCustomHighestTransactionIndex] =
    useState(0);
  const [companyName, setCompanyName] = useState("");
  const [customCompanyName, setCustomCompanyName] = useState("");

  // Unique ID for this hook instance — used in console logging so we can tell
  // which component triggered a load or save.
  const instanceId = useRef(`hook-${++hookInstanceCounter}`);

  // Default view state
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [liabilities, setLiabilities] = useState<Account[]>([]);
  const [equityAccounts, setEquityAccounts] = useState<Account[]>([]);
  const [equityAmounts, setEquityAmounts] = useState<Amount[]>([]);
  const [accountDroppedAmounts, setAccountDroppedAmounts] =
    useState<DroppedAmounts>({});
  const [liabilityDroppedAmounts, setLiabilityDroppedAmounts] =
    useState<DroppedAmounts>({});
  const [highlightedAmounts, setHighlightedAmounts] = useState<Amount | null>(
    null,
  );
  const [equityAccountDroppedAmounts, setEquityAccountDroppedAmounts] =
    useState<DroppedAmounts>({});
  const [expenseAccounts, setExpenseAccounts] = useState<Account[]>([]);
  const [incomeAccounts, setIncomeAccounts] = useState<Account[]>([]);
  const [expenseDroppedAmounts, setExpenseDroppedAmounts] =
    useState<DroppedAmounts>({});
  const [incomeDroppedAmounts, setIncomeDroppedAmounts] =
    useState<DroppedAmounts>({});
  const [transactionNotes, setTransactionNotes] = useState<
    Record<number, string>
  >({});
  const [customTransactionNotes, setCustomTransactionNotes] = useState<
    Record<number, string>
  >({});
  const [notesVersion, setNotesVersion] = useState(0);
  const [sectionHints, setSectionHints] = useState<Record<string, string>>({});

  // Custom view state
  const [customAccounts, setCustomAccounts] = useState<Account[]>([]);
  const [customLiabilities, setCustomLiabilities] = useState<Account[]>([]);
  const [customEquityAccounts, setCustomEquityAccounts] = useState<Account[]>(
    [],
  );
  const [customEquityAmounts, setCustomEquityAmounts] = useState<Amount[]>([]);
  const [customAccountDroppedAmounts, setCustomAccountDroppedAmounts] =
    useState<DroppedAmounts>({});
  const [customLiabilityDroppedAmounts, setCustomLiabilityDroppedAmounts] =
    useState<DroppedAmounts>({});
  const [
    customEquityAccountDroppedAmounts,
    setCustomEquityAccountDroppedAmounts,
  ] = useState<DroppedAmounts>({});
  const [customExpenseAccounts, setCustomExpenseAccounts] = useState<Account[]>(
    [],
  );
  const [customIncomeAccounts, setCustomIncomeAccounts] = useState<Account[]>(
    [],
  );
  const [customExpenseDroppedAmounts, setCustomExpenseDroppedAmounts] =
    useState<DroppedAmounts>({});
  const [customIncomeDroppedAmounts, setCustomIncomeDroppedAmounts] =
    useState<DroppedAmounts>({});

  // Get the current transaction index based on the view mode (reactive)
  const reactiveCurrentTransactionIndex = useMemo(() => {
    return viewMode === "custom"
      ? customTransactionIndex
      : currentTransactionIndex;
  }, [viewMode, customTransactionIndex, currentTransactionIndex]);

  // Save progress to Firebase.
  // NOTE: This is only called explicitly (e.g. after transfers / removals in
  // Dashboard).  There is NO automatic debounce timer — every individual
  // mutation (updateAccounts, updateLiabilities, …) already saves its own
  // data directly via UserProgressService, so a blanket auto-save would let
  // stale hook instances overwrite fresh data.
  const saveProgress = useCallback(async () => {
    if (!currentUser?.id || !cohort?.id) return;

    try {
      const progressData: Partial<UserProgressState> = {
        currentTransactionIndex,
        customTransactionIndex,
        viewMode,

        // Default view data
        accounts,
        liabilities,
        equityAccounts,
        equityAmounts,
        accountDroppedAmounts,
        liabilityDroppedAmounts,
        equityAccountDroppedAmounts,
        expenseAccounts,
        incomeAccounts,
        expenseDroppedAmounts,
        incomeDroppedAmounts,

        // Custom view data
        customAccounts,
        customLiabilities,
        customEquityAccounts,
        customEquityAmounts,
        customAccountDroppedAmounts,
        customLiabilityDroppedAmounts,
        customEquityAccountDroppedAmounts,
        customExpenseAccounts,
        customIncomeAccounts,
        customExpenseDroppedAmounts,
        customIncomeDroppedAmounts,
      };

      stampLocalWrite();
      await UserProgressService.saveUserProgress(
        currentUser.id,
        cohort.id,
        progressData as any,
      );
    } catch (error) {
      logger.error("Failed to save progress", error, "USER_PROGRESS");
    }
  }, [
    currentUser?.id,
    cohort?.id,
    currentTransactionIndex,
    customTransactionIndex,
    viewMode,
    // Default view state
    accounts,
    liabilities,
    equityAccounts,
    equityAmounts,
    accountDroppedAmounts,
    liabilityDroppedAmounts,
    equityAccountDroppedAmounts,
    expenseAccounts,
    incomeAccounts,
    expenseDroppedAmounts,
    incomeDroppedAmounts,
    // Custom view state
    customAccounts,
    customLiabilities,
    customEquityAccounts,
    customEquityAmounts,
    customAccountDroppedAmounts,
    customLiabilityDroppedAmounts,
    customEquityAccountDroppedAmounts,
    customExpenseAccounts,
    customIncomeAccounts,
    customExpenseDroppedAmounts,
    customIncomeDroppedAmounts,
  ]);

  // Load cohort view mode
  useEffect(() => {
    if (cohort?.id) {
      const fetchInitialViewMode = async () => {
        try {
          // Initial fetch to set the first value
          const mode = participantViewMode(
            await CohortService.getTransactionViewMode(cohort.id),
            (await CohortService.getCohortById(cohort.id))?.transactionConfig,
          );
          if (mode !== cohortViewModeRef.current) {
            cohortViewModeRef.current = mode;
            setCohortViewMode(mode);
          }
        } catch (error) {
          console.error("Error fetching initial view mode:", error);
          if (cohortViewModeRef.current !== "default") {
            cohortViewModeRef.current = "default";
            setCohortViewMode("default");
          }
        }
      };

      // Set up real-time listener
      const db = getFirestore();
      const cohortRef = doc(db, "cohorts", cohort.id);

      // Create the snapshot listener
      const unsubscribe = onSnapshot(
        cohortRef,
        (doc) => {
          if (doc.exists()) {
            const cohortData = doc.data();
            const newMode = participantViewMode(
              cohortData?.transactionViewMode,
              cohortData?.transactionConfig,
            );
            setHasCustomSet(
              (cohortData?.transactionConfig?.custom?.transactions?.length ??
                0) > 0,
            );

            // Only update if the mode has changed
            if (newMode !== cohortViewModeRef.current) {
              cohortViewModeRef.current = newMode;
              setCohortViewMode(newMode);

              // Dispatch a custom event to notify components of view mode change
              // This ensures all dependent components update immediately
              window.dispatchEvent(
                new CustomEvent("cohortViewModeChanged", {
                  detail: { newMode, cohortId: cohort.id },
                }),
              );
            }
          }
        },
        (error) => {
          console.error("Error in view mode snapshot listener:", error);
          // Default to "default" view mode if there's an error
          if (cohortViewModeRef.current !== "default") {
            cohortViewModeRef.current = "default";
            setCohortViewMode("default");
          }
        },
      );

      // Fetch initial state
      fetchInitialViewMode();

      // Cleanup function to unsubscribe from the snapshot listener
      return () => unsubscribe();
    }
  }, [cohort?.id]);

  // Load progress from Firebase
  const loadProgress = useCallback(async () => {
    if (!currentUser?.id || !cohort?.id) {
      return;
    }

    try {
      const progress = await UserProgressService.getUserProgress(
        currentUser.id,
        cohort.id,
      );

      if (progress) {
        // Default view data
        setCurrentTransactionIndex(progress.currentTransactionIndex || 0);
        setHighestTransactionIndex(progress.highestTransactionIndex || 0);
        setAccounts(progress.accounts || []);
        setLiabilities(progress.liabilities || []);
        setEquityAccounts(progress.equityAccounts || []);
        setEquityAmounts(progress.equityAmounts || []);
        setAccountDroppedAmounts(progress.accountDroppedAmounts || {});
        setLiabilityDroppedAmounts(progress.liabilityDroppedAmounts || {});
        setEquityAccountDroppedAmounts(
          progress.equityAccountDroppedAmounts || {},
        );
        setExpenseAccounts(progress.expenseAccounts || []);
        setIncomeAccounts(progress.incomeAccounts || []);
        setExpenseDroppedAmounts(progress.expenseDroppedAmounts || {});
        setIncomeDroppedAmounts(progress.incomeDroppedAmounts || {});
        setCompanyName(progress.companyName || "");
        setCustomCompanyName(progress.customCompanyName || "");

        setTransactionNotes(progress.transactionNotes || {});
        setCustomTransactionNotes(progress.customTransactionNotes || {});
        setSectionHints(progress.sectionHints || {});

        // Custom view data
        setCustomTransactionIndex(progress.customTransactionIndex || 0);
        setCustomHighestTransactionIndex(
          progress.customHighestTransactionIndex || 0,
        );
        setCustomAccounts(progress.customAccounts || []);
        setCustomLiabilities(progress.customLiabilities || []);
        setCustomEquityAccounts(progress.customEquityAccounts || []);
        setCustomEquityAmounts(progress.customEquityAmounts || []);
        setCustomAccountDroppedAmounts(
          progress.customAccountDroppedAmounts || {},
        );
        setCustomLiabilityDroppedAmounts(
          progress.customLiabilityDroppedAmounts || {},
        );
        setCustomEquityAccountDroppedAmounts(
          progress.customEquityAccountDroppedAmounts || {},
        );
        setCustomExpenseAccounts(progress.customExpenseAccounts || []);
        setCustomIncomeAccounts(progress.customIncomeAccounts || []);
        setCustomExpenseDroppedAmounts(
          progress.customExpenseDroppedAmounts || {},
        );
        setCustomIncomeDroppedAmounts(
          progress.customIncomeDroppedAmounts || {},
        );
      } else {
        // Initialize with empty data. The transaction index lives in a shared
        // context, so reset it too — otherwise a trainer opening a new course
        // keeps the position from the previous course, which can point past
        // the end of this course's transactions ("No Transactions").
        setCurrentTransactionIndex(0);
        setCustomTransactionIndex(0);
        stampLocalWrite();
        await UserProgressService.resetUserProgress(currentUser.id, cohort.id);
      }

      // Set isLoaded to true regardless of whether progress was found
      setIsLoaded(true);
      isLoadedRef.current = true;
    } catch (error) {
      console.error("Failed to load progress:", error);
      // Set isLoaded to true even if there was an error, so the UI doesn't stay in loading state
      setIsLoaded(true);
      isLoadedRef.current = true;
    }
  }, [currentUser?.id, cohort?.id]);

  // Subscribe to real-time updates from Firestore so that writes from other
  // hook instances (e.g. Dashboard writing while Financial Statements is
  // mounted, or just-before navigation) are reflected immediately without
  // waiting for a full page reload.  The first onSnapshot callback fires with
  // the local cache, which is equivalent to the old one-shot getDoc but also
  // keeps listening for subsequent changes.
  useEffect(() => {
    if (!currentUser?.id || !cohort?.id) return;

    // Still do initial load via loadProgress for the first paint (handles the
    // reset-if-missing logic).  The subscription below will keep state fresh
    // after that.
    loadProgress();

    const unsubscribe = UserProgressService.subscribeToUserProgress(
      currentUser.id,
      cohort.id,
      (progress) => {
        if (!progress) return; // doc deleted / doesn't exist — loadProgress handles init

        // Only apply Firestore updates after the initial load has completed.
        // Before that, loadProgress is in charge of setting state.
        if (!isLoadedRef.current) return;

        // Skip updates that arrive shortly after a local write from THIS hook
        // instance to avoid resetting optimistic in-memory state.  Hook
        // instances that don't write (e.g. Financial Statements) always accept.
        if (Date.now() - lastWriteRef.current < LOCAL_WRITE_COOLDOWN_MS) {
          return;
        }

        // Default view data
        setCurrentTransactionIndex(progress.currentTransactionIndex || 0);
        setHighestTransactionIndex(progress.highestTransactionIndex || 0);
        setAccounts(progress.accounts || []);
        setLiabilities(progress.liabilities || []);
        setEquityAccounts(progress.equityAccounts || []);
        setEquityAmounts(progress.equityAmounts || []);
        setAccountDroppedAmounts(progress.accountDroppedAmounts || {});
        setLiabilityDroppedAmounts(progress.liabilityDroppedAmounts || {});
        setEquityAccountDroppedAmounts(
          progress.equityAccountDroppedAmounts || {},
        );
        setExpenseAccounts(progress.expenseAccounts || []);
        setIncomeAccounts(progress.incomeAccounts || []);
        setExpenseDroppedAmounts(progress.expenseDroppedAmounts || {});
        setIncomeDroppedAmounts(progress.incomeDroppedAmounts || {});
        setCompanyName(progress.companyName || "");
        setCustomCompanyName(progress.customCompanyName || "");
        setTransactionNotes(progress.transactionNotes || {});
        setCustomTransactionNotes(progress.customTransactionNotes || {});
        setSectionHints(progress.sectionHints || {});

        // Custom view data
        setCustomTransactionIndex(progress.customTransactionIndex || 0);
        setCustomHighestTransactionIndex(
          progress.customHighestTransactionIndex || 0,
        );
        setCustomAccounts(progress.customAccounts || []);
        setCustomLiabilities(progress.customLiabilities || []);
        setCustomEquityAccounts(progress.customEquityAccounts || []);
        setCustomEquityAmounts(progress.customEquityAmounts || []);
        setCustomAccountDroppedAmounts(
          progress.customAccountDroppedAmounts || {},
        );
        setCustomLiabilityDroppedAmounts(
          progress.customLiabilityDroppedAmounts || {},
        );
        setCustomEquityAccountDroppedAmounts(
          progress.customEquityAccountDroppedAmounts || {},
        );
        setCustomExpenseAccounts(progress.customExpenseAccounts || []);
        setCustomIncomeAccounts(progress.customIncomeAccounts || []);
        setCustomExpenseDroppedAmounts(
          progress.customExpenseDroppedAmounts || {},
        );
        setCustomIncomeDroppedAmounts(
          progress.customIncomeDroppedAmounts || {},
        );
      },
    );

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, cohort?.id]);

  // --------------------------------------------------------------------------
  // AUTO-SAVE REMOVED
  //
  // Previously a 1000 ms debounce timer would call saveProgress() whenever
  // any piece of state changed.  Because useUserProgress() is instantiated
  // independently by Dashboard, UserSidebar, every AccountBox, and other
  // pages, each instance held its own (often stale) copy of the data.  When
  // *any* of those instances' timers fired — or when their unmount / before-
  // unload handlers ran — the stale snapshot would overwrite the most-recent
  // writes in Firestore.
  //
  // This is the root cause of the "navigate away and back → data gone" bug.
  //
  // Every individual mutation function (updateAccounts, updateLiabilities,
  // updateEquityAccounts, updateDroppedAmounts, …) already writes its own
  // field directly to Firestore via UserProgressService, so the auto-save
  // was fully redundant.  saveProgress() is still available for the few
  // explicit call-sites in Dashboard that need a full-document write (e.g.
  // after transfers or amount removals).
  // --------------------------------------------------------------------------

  // Getter functions based on view mode
  const getActiveAccounts = () =>
    viewMode === "custom" ? customAccounts : accounts;
  const getActiveLiabilities = () =>
    viewMode === "custom" ? customLiabilities : liabilities;
  const getActiveEquityAccounts = () =>
    viewMode === "custom" ? customEquityAccounts : equityAccounts;
  const getActiveEquityAmounts = () =>
    viewMode === "custom" ? customEquityAmounts : equityAmounts;
  const getActiveExpenseAccounts = () =>
    viewMode === "custom" ? customExpenseAccounts : expenseAccounts;
  const getActiveIncomeAccounts = () =>
    viewMode === "custom" ? customIncomeAccounts : incomeAccounts;

  // Setter functions based on view mode
  const setActiveAccounts = (newAccounts: Account[]) => {
    if (viewMode === "custom") {
      setCustomAccounts(newAccounts);
    } else {
      setAccounts(newAccounts);
    }
  };

  const setActiveLiabilities = (newLiabilities: Account[]) => {
    if (viewMode === "custom") {
      setCustomLiabilities(newLiabilities);
    } else {
      setLiabilities(newLiabilities);
    }
  };

  const setActiveEquityAccounts = (newEquityAccounts: Account[]) => {
    if (viewMode === "custom") {
      setCustomEquityAccounts(newEquityAccounts);
    } else {
      setEquityAccounts(newEquityAccounts);
    }
  };

  const setActiveEquityAmounts = (newEquityAmounts: Amount[]) => {
    if (viewMode === "custom") {
      setCustomEquityAmounts(newEquityAmounts);
    } else {
      setEquityAmounts(newEquityAmounts);
    }
  };

  // Reset progress function
  const resetProgress = useCallback(async () => {
    if (!currentUser?.id || !cohort?.id) return;

    try {
      // Reset all state to initial values
      setCurrentTransactionIndex(0);
      setHighestTransactionIndex(0);
      setCustomTransactionIndex(0);
      setCustomHighestTransactionIndex(0);
      setAccounts([]);
      setLiabilities([]);
      setEquityAccounts([]);
      setEquityAmounts([]);
      setAccountDroppedAmounts({});
      setLiabilityDroppedAmounts({});
      setEquityAccountDroppedAmounts({});
      setExpenseAccounts([]);
      setIncomeAccounts([]);
      setExpenseDroppedAmounts({});
      setIncomeDroppedAmounts({});
      setCustomAccounts([]);
      setCustomLiabilities([]);
      setCustomEquityAccounts([]);
      setCustomEquityAmounts([]);
      setCustomAccountDroppedAmounts({});
      setCustomLiabilityDroppedAmounts({});
      setCustomEquityAccountDroppedAmounts({});
      setCustomExpenseAccounts([]);
      setCustomIncomeAccounts([]);
      setCustomExpenseDroppedAmounts({});
      setCustomIncomeDroppedAmounts({});
      setTransactionNotes({});
      setCustomTransactionNotes({});

      // Save the reset state to the database
      stampLocalWrite();
      await UserProgressService.resetUserProgress(currentUser.id, cohort.id);
    } catch (error) {
      console.error("Failed to reset progress:", error);
    }
  }, [currentUser?.id, cohort?.id]);

  // Update company name
  const updateCompanyName = useCallback(
    async (name: string) => {
      if (!currentUser?.id || !cohort?.id) return;

      try {
        if (viewMode === "custom") {
          setCustomCompanyName(name);
        } else {
          setCompanyName(name);
        }
        stampLocalWrite();
        await UserProgressService.updateCompanyName(
          currentUser.id,
          cohort.id,
          name,
          viewMode,
        );
      } catch (error) {
        console.error("Failed to update company name:", error);
      }
    },
    [currentUser?.id, cohort?.id, viewMode],
  );

  // Update transaction index
  const setTransactionIndex = useCallback(
    async (index: number) => {
      if (!currentUser?.id || !cohort?.id) return;

      try {
        if (viewMode === "custom") {
          setCustomTransactionIndex(index);
          // Update highest if navigating to a higher transaction
          if (index > customHighestTransactionIndex) {
            setCustomHighestTransactionIndex(index);
          }
        } else {
          setCurrentTransactionIndex(index);
          // Update highest if navigating to a higher transaction
          if (index > highestTransactionIndex) {
            setHighestTransactionIndex(index);
          }
        }

        stampLocalWrite();
        await UserProgressService.updateTransactionIndex(
          currentUser.id,
          cohort.id,
          index,
          viewMode,
        );
      } catch (error) {
        // Silently handle - UI state is already updated
      }
    },
    [
      currentUser?.id,
      cohort?.id,
      viewMode,
      accounts,
      customTransactionIndex,
      currentTransactionIndex,
      highestTransactionIndex,
      customHighestTransactionIndex,
    ],
  );

  // Update accounts
  const updateAccounts = useCallback(
    async (newAccounts: Account[]) => {
      if (!currentUser?.id || !cohort?.id) return;

      try {
        if (viewMode === "custom") {
          setCustomAccounts(newAccounts);
        } else {
          setAccounts(newAccounts);
        }

        stampLocalWrite();
        await UserProgressService.updateAccounts(
          currentUser.id,
          cohort.id,
          newAccounts,
          viewMode,
        );

        // Trigger snapshot save immediately with fresh account data

        if (
          typeof window !== "undefined" &&
          (window as any).triggerSnapshotSaveWithAccounts
        ) {
          const activeLiabilities =
            viewMode === "custom" ? customLiabilities : liabilities;

          (window as any).triggerSnapshotSaveWithAccounts(
            newAccounts,
            activeLiabilities,
          );
        }
      } catch (error) {
        logger.error("Failed to update accounts", error, "USER_PROGRESS");
      }
    },
    [currentUser?.id, cohort?.id, viewMode, liabilities, customLiabilities],
  );

  // Update liabilities
  const updateLiabilities = useCallback(
    async (newLiabilities: Account[]) => {
      if (!currentUser?.id || !cohort?.id) return;

      try {
        if (viewMode === "custom") {
          setCustomLiabilities(newLiabilities);
        } else {
          setLiabilities(newLiabilities);
        }

        stampLocalWrite();
        await UserProgressService.updateLiabilities(
          currentUser.id,
          cohort.id,
          newLiabilities,
          viewMode,
        );

        // Trigger snapshot save immediately with fresh liability data

        if (
          typeof window !== "undefined" &&
          (window as any).triggerSnapshotSaveWithLiabilities
        ) {
          const activeAccounts =
            viewMode === "custom" ? customAccounts : accounts;

          (window as any).triggerSnapshotSaveWithLiabilities(
            newLiabilities,
            activeAccounts,
          );
        }
      } catch (error) {
        logger.error("Failed to update liabilities", error, "USER_PROGRESS");
      }
    },
    [currentUser?.id, cohort?.id, viewMode, accounts, customAccounts],
  );

  // Update equity accounts
  const updateEquityAccounts = useCallback(
    async (newEquityAccounts: Account[]) => {
      if (!currentUser?.id || !cohort?.id) return;

      try {
        if (viewMode === "custom") {
          setCustomEquityAccounts(newEquityAccounts);
        } else {
          setEquityAccounts(newEquityAccounts);
        }

        stampLocalWrite();
        await UserProgressService.updateEquityAccounts(
          currentUser.id,
          cohort.id,
          newEquityAccounts,
          viewMode,
        );

        if (
          typeof window !== "undefined" &&
          (window as any).triggerSnapshotSaveWithEquity
        ) {
          const activeAccounts =
            viewMode === "custom" ? customAccounts : accounts;
          const activeLiabilities =
            viewMode === "custom" ? customLiabilities : liabilities;

          (window as any).triggerSnapshotSaveWithEquity(
            newEquityAccounts,
            activeAccounts,
            activeLiabilities,
          );
        }
      } catch (error) {
        console.error("Failed to update equity accounts:", error);
      }
    },
    [
      currentUser?.id,
      cohort?.id,
      viewMode,
      accounts,
      liabilities,
      customAccounts,
      customLiabilities,
    ],
  );

  // Update equity amounts
  const updateEquityAmounts = useCallback(
    async (newEquityAmounts: Amount[]) => {
      if (!currentUser?.id || !cohort?.id) return;

      try {
        if (viewMode === "custom") {
          setCustomEquityAmounts(newEquityAmounts);
        } else {
          setEquityAmounts(newEquityAmounts);
        }

        stampLocalWrite();
        await UserProgressService.updateEquityAmounts(
          currentUser.id,
          cohort.id,
          newEquityAmounts,
          viewMode,
        );
      } catch (error) {
        console.error("Failed to update equity amounts:", error);
      }
    },
    [currentUser?.id, cohort?.id, viewMode],
  );

  // Update account dropped amounts
  const updateAccountDroppedAmounts = useCallback(
    async (newDroppedAmounts: DroppedAmounts) => {
      if (!currentUser?.id || !cohort?.id) return;

      try {
        const totalAmounts = Object.values(newDroppedAmounts).reduce(
          (sum, arr) => sum + arr.length,
          0,
        );

        if (viewMode === "custom") {
          setCustomAccountDroppedAmounts(newDroppedAmounts);
        } else {
          setAccountDroppedAmounts(newDroppedAmounts);
        }

        stampLocalWrite();
        await UserProgressService.updateAccountDroppedAmounts(
          currentUser.id,
          cohort.id,
          newDroppedAmounts,
          viewMode,
        );
      } catch (error) {
        console.error("Failed to update account dropped amounts:", error);
      }
    },
    [currentUser?.id, cohort?.id, viewMode],
  );

  // Update liability dropped amounts
  const updateLiabilityDroppedAmounts = useCallback(
    async (newDroppedAmounts: DroppedAmounts) => {
      if (!currentUser?.id || !cohort?.id) return;

      try {
        if (viewMode === "custom") {
          setCustomLiabilityDroppedAmounts(newDroppedAmounts);
        } else {
          setLiabilityDroppedAmounts(newDroppedAmounts);
        }

        stampLocalWrite();
        await UserProgressService.updateLiabilityDroppedAmounts(
          currentUser.id,
          cohort.id,
          newDroppedAmounts,
          viewMode,
        );
      } catch (error) {
        console.error("Failed to update liability dropped amounts:", error);
      }
    },
    [currentUser?.id, cohort?.id, viewMode],
  );

  // Update equity account dropped amounts
  const updateEquityAccountDroppedAmounts = useCallback(
    async (newDroppedAmounts: DroppedAmounts) => {
      if (!currentUser?.id || !cohort?.id) return;

      try {
        if (viewMode === "custom") {
          setCustomEquityAccountDroppedAmounts(newDroppedAmounts);
        } else {
          setEquityAccountDroppedAmounts(newDroppedAmounts);
        }

        stampLocalWrite();
        await UserProgressService.updateEquityAccountDroppedAmounts(
          currentUser.id,
          cohort.id,
          newDroppedAmounts,
          viewMode,
        );
      } catch (error) {
        console.error(
          "Failed to update equity account dropped amounts:",
          error,
        );
      }
    },
    [currentUser?.id, cohort?.id, viewMode],
  );

  // Update expense accounts
  const updateExpenseAccounts = useCallback(
    async (newExpenseAccounts: Account[]) => {
      if (!currentUser?.id || !cohort?.id) return;

      try {
        if (viewMode === "custom") {
          setCustomExpenseAccounts(newExpenseAccounts);
        } else {
          setExpenseAccounts(newExpenseAccounts);
        }

        stampLocalWrite();
        await UserProgressService.updateExpenseAccounts(
          currentUser.id,
          cohort.id,
          newExpenseAccounts,
          viewMode,
        );

        // Trigger snapshot save immediately with fresh expense data
        if (
          typeof window !== "undefined" &&
          (window as any).triggerSnapshotSaveWithExpenses
        ) {
          (window as any).triggerSnapshotSaveWithExpenses(newExpenseAccounts);
        }
      } catch (error) {
        console.error("Failed to update expense accounts:", error);
      }
    },
    [currentUser?.id, cohort?.id, viewMode],
  );

  // Update income accounts
  const updateIncomeAccounts = useCallback(
    async (newIncomeAccounts: Account[]) => {
      if (!currentUser?.id || !cohort?.id) return;

      try {
        if (viewMode === "custom") {
          setCustomIncomeAccounts(newIncomeAccounts);
        } else {
          setIncomeAccounts(newIncomeAccounts);
        }

        stampLocalWrite();
        await UserProgressService.updateIncomeAccounts(
          currentUser.id,
          cohort.id,
          newIncomeAccounts,
          viewMode,
        );

        // Trigger snapshot save immediately with fresh income data
        if (
          typeof window !== "undefined" &&
          (window as any).triggerSnapshotSaveWithIncome
        ) {
          (window as any).triggerSnapshotSaveWithIncome(newIncomeAccounts);
        }
      } catch (error) {
        console.error("Failed to update income accounts:", error);
      }
    },
    [currentUser?.id, cohort?.id, viewMode],
  );

  // Update expense dropped amounts
  const updateExpenseDroppedAmounts = useCallback(
    async (newDroppedAmounts: DroppedAmounts) => {
      if (!currentUser?.id || !cohort?.id) return;

      try {
        if (viewMode === "custom") {
          setCustomExpenseDroppedAmounts(newDroppedAmounts);
        } else {
          setExpenseDroppedAmounts(newDroppedAmounts);
        }

        stampLocalWrite();
        await UserProgressService.updateExpenseDroppedAmounts(
          currentUser.id,
          cohort.id,
          newDroppedAmounts,
          viewMode,
        );
      } catch (error) {
        console.error("Failed to update expense dropped amounts:", error);
      }
    },
    [currentUser?.id, cohort?.id, viewMode],
  );

  // Update income dropped amounts
  const updateIncomeDroppedAmounts = useCallback(
    async (newDroppedAmounts: DroppedAmounts) => {
      if (!currentUser?.id || !cohort?.id) return;

      try {
        if (viewMode === "custom") {
          setCustomIncomeDroppedAmounts(newDroppedAmounts);
        } else {
          setIncomeDroppedAmounts(newDroppedAmounts);
        }

        stampLocalWrite();
        await UserProgressService.updateIncomeDroppedAmounts(
          currentUser.id,
          cohort.id,
          newDroppedAmounts,
          viewMode,
        );
      } catch (error) {
        console.error("Failed to update income dropped amounts:", error);
      }
    },
    [currentUser?.id, cohort?.id, viewMode],
  );

  // Label/comment edits must start from the active set's accounts. Using the
  // primary lists here wrote the primary accounts into the custom set (and
  // wiped its accounts) whenever a label or comment was set in Custom view.
  const activeAccounts = viewMode === "custom" ? customAccounts : accounts;
  const activeLiabilities =
    viewMode === "custom" ? customLiabilities : liabilities;
  const activeEquityAccounts =
    viewMode === "custom" ? customEquityAccounts : equityAccounts;
  const activeExpenseAccounts =
    viewMode === "custom" ? customExpenseAccounts : expenseAccounts;
  const activeIncomeAccounts =
    viewMode === "custom" ? customIncomeAccounts : incomeAccounts;

  const updateAccountComment = useCallback(
    (
      accountId: string,
      comment: string,
      accountType: "assets" | "liabilities" | "equity" | "expenses" | "income",
    ) => {
      switch (accountType) {
        case "assets":
          updateAccounts(
            activeAccounts.map((account) =>
              account.id === accountId ? { ...account, comment } : account,
            ),
          );
          break;
        case "liabilities":
          updateLiabilities(
            activeLiabilities.map((liability) =>
              liability.id === accountId
                ? { ...liability, comment }
                : liability,
            ),
          );
          break;
        case "equity":
          updateEquityAccounts(
            activeEquityAccounts.map((equity) =>
              equity.id === accountId ? { ...equity, comment } : equity,
            ),
          );
          break;
        case "expenses":
          updateExpenseAccounts(
            activeExpenseAccounts.map((expense) =>
              expense.id === accountId ? { ...expense, comment } : expense,
            ),
          );
          break;
        case "income":
          updateIncomeAccounts(
            activeIncomeAccounts.map((income) =>
              income.id === accountId ? { ...income, comment } : income,
            ),
          );
          break;
      }
    },
    [
      activeAccounts,
      activeLiabilities,
      activeEquityAccounts,
      activeExpenseAccounts,
      activeIncomeAccounts,
      updateAccounts,
      updateLiabilities,
      updateEquityAccounts,
      updateExpenseAccounts,
      updateIncomeAccounts,
    ],
  );

  const updateAccountDropdownSelection = useCallback(
    (
      accountId: string,
      selection: string,
      accountType: "assets" | "liabilities" | "equity" | "expenses" | "income",
      currentName?: string, // Optional: pass the current name to avoid race condition with React state
    ) => {
      // Helper to build updated account with optional name override
      const buildUpdatedAccount = (account: any) => {
        const updated = { ...account, label: selection };
        // If currentName is provided and the account name is empty, use currentName
        if (currentName && (!account.name || account.name === "")) {
          updated.name = currentName;
        }
        return updated;
      };

      switch (accountType) {
        case "assets":
          updateAccounts(
            activeAccounts.map((account) =>
              account.id === accountId ? buildUpdatedAccount(account) : account,
            ),
          );
          break;
        case "liabilities":
          updateLiabilities(
            activeLiabilities.map((liability) =>
              liability.id === accountId
                ? buildUpdatedAccount(liability)
                : liability,
            ),
          );
          break;
        case "equity":
          updateEquityAccounts(
            activeEquityAccounts.map((equity) =>
              equity.id === accountId ? buildUpdatedAccount(equity) : equity,
            ),
          );
          break;
        case "expenses":
          updateExpenseAccounts(
            activeExpenseAccounts.map((expense) =>
              expense.id === accountId ? buildUpdatedAccount(expense) : expense,
            ),
          );
          break;
        case "income":
          updateIncomeAccounts(
            activeIncomeAccounts.map((income) =>
              income.id === accountId ? buildUpdatedAccount(income) : income,
            ),
          );
          break;
      }

      // Notify Financial Statements page that accounts have changed
      const event = new CustomEvent("accountsChanged", {
        detail: { accountId, selection, accountType },
      });
      window.dispatchEvent(event);
    },
    [
      activeAccounts,
      activeLiabilities,
      activeEquityAccounts,
      activeExpenseAccounts,
      activeIncomeAccounts,
      updateAccounts,
      updateLiabilities,
      updateEquityAccounts,
      updateExpenseAccounts,
      updateIncomeAccounts,
    ],
  );

  const activeTransactionNotes =
    viewMode === "custom" ? customTransactionNotes : transactionNotes;

  return {
    isLoaded,
    cohort,
    // Primary and custom each have their own company name.
    companyName: viewMode === "custom" ? customCompanyName : companyName,
    setCompanyName:
      viewMode === "custom" ? setCustomCompanyName : setCompanyName,

    // Transaction indexes
    currentTransactionIndex: reactiveCurrentTransactionIndex,
    customTransactionIndex,
    setCurrentTransactionIndex:
      viewMode === "custom"
        ? setCustomTransactionIndex
        : setCurrentTransactionIndex,

    // Highest transaction index (for revealing transactions in summary)
    highestTransactionIndex:
      viewMode === "custom"
        ? customHighestTransactionIndex
        : highestTransactionIndex,

    // What the app should act on: the user's own pick from
    // TransactionViewSwitch when they've made one, else the cohort setting.
    viewMode,
    cohortViewMode,

    // Active data based on current view mode
    accounts: getActiveAccounts(),
    setAccounts: setActiveAccounts,
    liabilities: getActiveLiabilities(),
    setLiabilities: setActiveLiabilities,
    equityAccounts: getActiveEquityAccounts(),
    setEquityAccounts: setActiveEquityAccounts,
    equityAmounts: getActiveEquityAmounts(),
    setEquityAmounts: setActiveEquityAmounts,

    // Access to both data sets
    defaultAccounts: accounts,
    customAccounts,
    defaultLiabilities: liabilities,
    customLiabilities,

    // Account amounts
    accountDroppedAmounts:
      viewMode === "custom"
        ? customAccountDroppedAmounts
        : accountDroppedAmounts,
    setAccountDroppedAmounts:
      viewMode === "custom"
        ? setCustomAccountDroppedAmounts
        : setAccountDroppedAmounts,
    liabilityDroppedAmounts:
      viewMode === "custom"
        ? customLiabilityDroppedAmounts
        : liabilityDroppedAmounts,
    setLiabilityDroppedAmounts:
      viewMode === "custom"
        ? setCustomLiabilityDroppedAmounts
        : setLiabilityDroppedAmounts,
    equityAccountDroppedAmounts:
      viewMode === "custom"
        ? customEquityAccountDroppedAmounts
        : equityAccountDroppedAmounts,
    setEquityAccountDroppedAmounts:
      viewMode === "custom"
        ? setCustomEquityAccountDroppedAmounts
        : setEquityAccountDroppedAmounts,

    // Expense and income
    expenseAccounts: getActiveExpenseAccounts(),
    setExpenseAccounts:
      viewMode === "custom" ? setCustomExpenseAccounts : setExpenseAccounts,
    incomeAccounts: getActiveIncomeAccounts(),
    setIncomeAccounts:
      viewMode === "custom" ? setCustomIncomeAccounts : setIncomeAccounts,
    expenseDroppedAmounts:
      viewMode === "custom"
        ? customExpenseDroppedAmounts
        : expenseDroppedAmounts,
    setExpenseDroppedAmounts:
      viewMode === "custom"
        ? setCustomExpenseDroppedAmounts
        : setExpenseDroppedAmounts,
    incomeDroppedAmounts:
      viewMode === "custom" ? customIncomeDroppedAmounts : incomeDroppedAmounts,
    setIncomeDroppedAmounts:
      viewMode === "custom"
        ? setCustomIncomeDroppedAmounts
        : setIncomeDroppedAmounts,

    // Actions
    saveProgress,
    loadProgress,

    // Additional methods needed by other components
    resetProgress,
    updateCompanyName,
    setTransactionIndex,
    updateAccounts,
    updateLiabilities,
    updateEquityAccounts,
    updateEquityAmounts,
    updateAccountDroppedAmounts,
    updateLiabilityDroppedAmounts,
    updateEquityAccountDroppedAmounts,
    updateExpenseAccounts,
    updateIncomeAccounts,
    updateExpenseDroppedAmounts,
    updateIncomeDroppedAmounts,
    updateAccountComment,
    updateAccountDropdownSelection,
    highlightedAmounts,
    setHighlightedAmounts,
    // Notes belong to the active set: primary and custom transactions share
    // ids (both count from 1), so each set keeps its own notes.
    transactionNotes: activeTransactionNotes,
    notesVersion,
    saveTransactionNote: async (transactionId: number, note: string) => {
      if (!currentUser || !cohort) return;

      try {
        stampLocalWrite();
        await UserProgressService.saveTransactionNote(
          currentUser.id,
          cohort.id,
          transactionId,
          note,
          viewMode,
        );
        const setNotes =
          viewMode === "custom" ? setCustomTransactionNotes : setTransactionNotes;
        setNotes((prev) => ({ ...prev, [transactionId]: note }));
        setNotesVersion((prev) => prev + 1);
      } catch (error) {
        console.error("Error in saveTransactionNote:", error);
        throw error;
      }
    },
    getTransactionNote: (transactionId: number) => {
      return activeTransactionNotes[transactionId] || "";
    },
    sectionHints,
    updateSectionHint: async (sectionType: string, hint: string) => {
      if (!currentUser || !cohort) return;

      try {
        setSectionHints((prev) => ({ ...prev, [sectionType]: hint }));
        stampLocalWrite();
        await UserProgressService.updateSectionHint(
          currentUser.id,
          cohort.id,
          sectionType,
          hint,
        );
      } catch (error) {
        console.error("Error in updateSectionHint:", error);
        throw error;
      }
    },
  };
};
